import { Col, Row, Space } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { SalesforceAutomation } from 'api/integrations';
import { ReactComponent as SalesforceLogo } from 'assets/svgs/salesforce-logo.svg';
import { Button } from 'components/atoms/button';
import { Page } from 'components/molecules/page';
import {
  IntegrationHeader,
  PageContent,
} from 'components/organisms/module-settings/integrations/components';
import { useTranslation } from 'hooks/use-translation';
import { getSalesforceAutomationUrl } from 'utils/urls';
import { AutomationActions as Actions } from './actions';
import { AutomationMatches as AutomationMatcheConditions } from './matches';
import { SalesForceObject } from './salesforce-object';
import { AutomationTrigger as Trigger } from './trigger';
import { useCreateAutomationActions } from './use-automation-create';
import { WorkflowName } from './workflow-name';

const Footer = styled.div`
  border-top: 1px solid var(--cool-gray-30);
  background-color: var(--white-100);
  padding: var(--spacing-05);
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  margin-left: 48px;
`;

export const EditAutomation = ({
  integrationId,
  automationById,
}: {
  integrationId: number;
  automationById: SalesforceAutomation;
}): JSX.Element => {
  const title = useTranslation(
    'settings.integrations.sf.automation.create.title',
  );
  const breadcrumbTitle = useTranslation('settings.integrations.sf.title');

  const {
    automationDetails,
    disableCreation,
    setAutomationDetails,
    updationStatus,
    onAutomationUpdate,
    salesforceObjects,
    salesforceObjectsStatus,
    salesforceTriggerFields,
    salesforceTriggerFieldsStatus,
    salesforceMatchFields,
    salesforceMatchFieldsStatus,
    salesforceActionFields,
    salesforceActionFieldsStatus,
    salesForceActionFieldMergedData,
  } = useCreateAutomationActions(integrationId, automationById);

  return (
    <Page>
      <IntegrationHeader
        title={automationDetails?.automationName ?? title}
        icon={<SalesforceLogo />}
        breadcrumbLink={getSalesforceAutomationUrl(integrationId.toString())}
        breadcrumbText={breadcrumbTitle}
      />
      <PageContent>
        <div style={{ paddingBottom: '200px' }}>
          <WorkflowName
            integrationId={integrationId}
            onChange={setAutomationDetails}
            automationDetails={automationDetails}
          />
          <SalesForceObject
            data={salesforceObjects}
            apiStatus={salesforceObjectsStatus}
            onChange={setAutomationDetails}
            automationDetails={automationDetails}
          />
          <Trigger
            data={salesforceTriggerFields}
            apiStatus={salesforceTriggerFieldsStatus}
            automationDetails={automationDetails}
            onChange={setAutomationDetails}
          />
          <AutomationMatcheConditions
            data={salesforceMatchFields}
            apiStatus={salesforceMatchFieldsStatus}
            automationDetails={automationDetails}
            onChange={setAutomationDetails}
          />
          <Actions
            requiredFields={salesforceActionFields}
            data={salesForceActionFieldMergedData}
            apiStatus={salesforceActionFieldsStatus}
            automationDetails={automationDetails}
            onChange={setAutomationDetails}
          />
          <Footer>
            <Row justify="end">
              <Col offset={4}>
                <Space>
                  <Link
                    to={getSalesforceAutomationUrl(integrationId.toString())}
                  >
                    <Button disabled={updationStatus === 'loading'}>
                      <FormattedMessage id="cancel" />
                    </Button>
                  </Link>
                  <Button
                    loading={updationStatus === 'loading'}
                    type="primary"
                    disabled={disableCreation}
                    onClick={onAutomationUpdate}
                  >
                    <FormattedMessage id="save" />
                  </Button>
                </Space>
              </Col>
            </Row>
          </Footer>
        </div>
      </PageContent>
    </Page>
  );
};
